import { LiterallyAnythingInput } from './Tabs/LiterallyAnythingInput';
import { BooksInput } from './Tabs/BooksInput';
import { TextOrLinkInput } from './Tabs/TextOrLinkInput';
import { VocabularyInput } from './Tabs/VocabularyInput';
import { LITERALLY_ANYTHING_ID, PacketInputProvider, TABS } from './Contexts/PacketInputContext';
import { PacketBaseProvider } from './Contexts/PacketBaseContext';
import { usePacketInput } from './Contexts/PacketInputContext';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InputContainer } from './Components/InputContainer';
import { HomepageModalsProvider, useHomepageModals } from './Contexts/HomepageModalsContext';
import { DiffitBooksProvider } from './Contexts/DiffitBooksContext';
import { useGenerateResources } from './Hooks/useGenerateResource';
import { Mixpanel } from '../../services/Mixpanel';
import { TemplateProvider } from '../Packet/Contexts/TemplateContext';
import readingLevels from '../../../public/json/reading-levels.json';
import { NAVBAR_HEIGHT_STYLE } from '../../utils/layout';

export const Homepage = () => {
  return (
    <DiffitBooksProvider>
      <TemplateProvider>
        <PacketBaseProvider>
          <PacketInputProvider>
            <HomepageModalsProvider>
              <HomepageInner />
            </HomepageModalsProvider>
          </PacketInputProvider>
        </PacketBaseProvider>
      </TemplateProvider>
    </DiffitBooksProvider>
  );
};

const HomepageInner = () => {
  const { setInputType, setTopic } = usePacketInput();
  const [query, setQuery] = useSearchParams();
  const { handleGenerateResources } = useGenerateResources();

  useEffect(() => {
    let timeoutId = null;
    const topicQuery = query.get('topic');
    const readingLevelQuery = query.get('readingLevel');
    if (topicQuery) {
      // remove the query params first so we don't run this useEffect again on page load
      query.delete('topic');
      query.delete('readingLevel');
      setQuery(query);

      // set the topic in the input, just for visual feedback for the user
      setTopic(topicQuery);

      timeoutId = setTimeout(() => {
        let overrides = {
          topic: topicQuery,
          customMessage: `Creating a resource just for you about ${topicQuery}`,
        };
        if (readingLevelQuery) {
          const readingLevel = readingLevels.find(
            (level) => level.id === parseInt(readingLevelQuery)
          );
          if (readingLevel) {
            // if the reading level is found, use it. otherwise we'll use whatever the defualt is
            // (either the user's last used reading level or the default 5th grade)
            overrides.readingLevel = readingLevel;
          }
        }
        handleGenerateResources(overrides);
      }, 800);
      return;
    }

    let tab = TABS.find((tab) => tab.id === LITERALLY_ANYTHING_ID);
    const hash = window.location.hash;
    if (hash) {
      const hashValue = hash.slice(1);
      const foundTab = TABS.find((tab) => tab.hash === hashValue);
      // Only use the found tab if it exists, otherwise fall back to default
      if (foundTab) {
        tab = foundTab;
      } else {
        // Invalid hash, redirect to default tab
        window.location.hash = '#' + tab.hash;
      }
    } else {
      window.location.hash = '#' + tab.hash;
    }
    setInputType(tab);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [query, handleGenerateResources]);

  return (
    <>
      <div
        className='flex flex-col items-center justify-start pt-12 md:pt-12 bg-gray-50'
        style={{ minHeight: NAVBAR_HEIGHT_STYLE }}>
        <div className='max-w-[1000px] w-full mx-auto text-gray-900 px-4 md:px-6 lg:px-8 flex flex-col items-center justify-start gap-y-8 flex-grow'>
          <div className='justify-center text-2xl font-normal'>Student-ready resources for...</div>
          <Tabs />
          <InputContainer>
            <LiterallyAnythingInput />
            <BooksInput />
            <TextOrLinkInput />
            <VocabularyInput />
          </InputContainer>
          <GenerateButton />
        </div>
      </div>
    </>
  );
};

const Tabs = () => {
  const { setInputType, inputType } = usePacketInput();
  const setTab = (tab) => {
    setInputType(tab);
    window.location.hash = '#' + tab.hash;
  };
  const { getIsActiveTab } = usePacketInput();

  return (
    <>
      <div className='flex items-center text-sm md:text-base bg-gray-100 rounded-xl border  border-gray-200 max-w-full overflow-x-auto scrollbar-minimal overflow-y-hidden h-fit'>
        {TABS.map((tab) => {
          const isActive = getIsActiveTab(tab);
          return (
            <div key={tab.id} className={`py-1 ${isActive ? 'bg-white rounded-xl' : ''}`}>
              <button
                key={tab.id}
                onClick={() => {
                  Mixpanel.track('Tab click', { from: inputType.id, to: tab.id });
                  setTab(tab);
                }}
                className={`mx-2 flex items-center py-2 gap-x-1 ${isActive ? 'text-gray-900' : 'hover:bg-gray-200 text-gray-700 hover:text-gray-900  '} whitespace-nowrap  rounded-lg p-1 px-2.5`}>
                <tab.icon className='w-4 h-4 min-w-4 min-h-4 ' />
                <span>{tab.text}</span>
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
};

const GenerateButton = () => {
  const { handleGenerateResources } = useGenerateResources();
  const { isGenerating } = useHomepageModals();

  return (
    <button
      onClick={() => {
        handleGenerateResources();
      }}
      disabled={isGenerating['isGenerating']}
      className='mt-1 bg-emerald-600 hover:bg-emerald-700 text-white px-12 py-3 rounded-xl font-semibold transition-colors'>
      Generate Resources
    </button>
  );
};
